import fetchInternalPaymentRequest from '../services/paymentRequest/fetchInternalPaymentRequest';
import fetchPaymentRequest from '../services/paymentRequest/fetchPaymentRequest';
import { Nullable } from '../types';
import isInternalPaymentRequest from '../validators/isInternalPaymentRequest';
import getRollbarInstance from './getRollbarInstance';

const DEFAULT_START_INTERVAL = 3;
const DEFAULT_TIMEOUT_INTERVAL = 30;

/**
 * @description Polls a PR for changes on the server determined by the `options.diffCheck` function
 * passed.
 */
export default function pollForPrUpdate(
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
  options: {
    /**
     * @description tests one PR against another to determine if it has changed.
     */
    diffCheck: (
      oldPr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
      newPr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
    ) => boolean,
    /**
     * @description Callback when PR has updated according to diffCheck function.
     */
    onChange: (
      pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
    ) => void,
    /**
     * @description Callback for when polling times out.
     */
    onTimeout?: () => void,
    /**
     * @description The length of time in seconds before the first poll should occur
     * @default 3
     */
    startInterval?: number,
    /**
     * @description The length of time in seconds when to timeout polling after beginning.
     * @default 30
     */
    timeoutInterval?: number,
  },
): () => void {
  return (function pollForPrUpdateEffect(): () => void {
    let timerId: Nullable<ReturnType<typeof setTimeout>>;
    let lastInterval = (options.startInterval
      ? options.startInterval : DEFAULT_START_INTERVAL) * 1000;

    const timeout = (
      options.timeoutInterval ? options.timeoutInterval : DEFAULT_TIMEOUT_INTERVAL
    ) * 1000;

    const timeoutTime = Date.now() + timeout;

    function poller(): void {
      if (Date.now() >= timeoutTime) {
        if (options.onTimeout) {
          options.onTimeout();
        }
        return;
      }
      (isInternalPaymentRequest(pr)
        ? fetchInternalPaymentRequest(pr.payment_request.id)
        : fetchPaymentRequest(pr.id)
      ).then((updatedPr) => {
        const diff = options.diffCheck(pr, updatedPr);
        if (diff) {
          options.onChange(updatedPr);
          return undefined;
        }
        // if the timer has not been cleared, safe to poll again.
        if (timerId !== null) {
          // backoff polling exponentially
          lastInterval *= 1.5;
          timerId = setTimeout(poller, Math.round(lastInterval));
        }
        return undefined;
      }).catch((error) => {
        const message = error instanceof Error ? error.message : 'Unkown error';
        getRollbarInstance().warn(`pollForPrUpdate: ${message}`);
      });
    }

    timerId = setTimeout(() => poller(), lastInterval);

    return (): void => {
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };
  }());
}
